/** Server-backed LandGate search results converted to map-only SiteLayers. */
import { authedFetch } from '../../lib/authedFetch';
import { computeLayerContentHash, mergeImportedLayers } from '../kmzParser';
import { hashCanonicalJson } from '../../lib/reportGeneration/reportContractHash';
import type { SiteLayer, SiteLayerCategory } from '../../types/site';
import type {
  LandGateGeoJsonFeature,
  LandGateGeoJsonGeometry,
  LandGateSiteSearchResponse,
} from './landGateTypes';
import { landGateConnectionErrorFromResponse } from './landGateConnectionClient';

const SAFE_ATTRIBUTE_KEYS = new Set([
  'parcel_id', 'parcelid', 'apn', 'acreage', 'acres', 'owner', 'owner_name',
  'zoning', 'zoning_code', 'jurisdiction', 'constraint_type', 'present',
  'infrastructure_type', 'name', 'label', 'feature_type', 'category',
  'data_vintage', 'source_date',
]);

export function isLandGateImportEnabled(): boolean {
  const viteEnv = typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env as Record<string, string | undefined>
    : undefined;
  const raw = viteEnv?.VITE_LANDGATE_IMPORT_ENABLED
    ?? (typeof process !== 'undefined' ? process.env.LANDGATE_IMPORT_ENABLED : undefined);
  if (typeof raw === 'string') return raw.trim().toLowerCase() === 'true';
  const mode = viteEnv?.MODE ?? (typeof process !== 'undefined' ? process.env.NODE_ENV : undefined);
  return mode === 'development' || mode === 'test';
}

function safeText(value: unknown, max = 160): string | undefined {
  if (value == null) return undefined;
  const text = String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
  return text || undefined;
}

function safeAttributes(
  properties: Record<string, unknown> | null | undefined,
): Record<string, string | number | boolean | null> | undefined {
  if (!properties) return undefined;
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (!SAFE_ATTRIBUTE_KEYS.has(key.toLowerCase())) continue;
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    else if (typeof value === 'boolean') out[key] = value;
    else {
      const text = safeText(value);
      if (text) out[key] = text;
    }
    if (Object.keys(out).length >= 25) break;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function categoryForFeature(feature: LandGateGeoJsonFeature): SiteLayerCategory {
  const props = feature.properties ?? {};
  const raw = safeText(props.category ?? props.feature_type)?.toLowerCase() ?? '';
  if (/pipeline|gas|hydrogen/.test(raw)) return 'infrastructure.pipeline';
  if (/electric|transmission|substation|power/.test(raw)) return 'infrastructure.electrical';
  if (/fiber|telecom/.test(raw)) return 'infrastructure.fiber';
  if (/road|rail|transport/.test(raw)) return 'infrastructure.transport';
  if (/wetland|flood|constraint|protected/.test(raw)) return 'constraint.environmental';
  return 'other';
}

function latLng(pair: readonly number[]): { lat: number; lng: number } | null {
  const lng = Number(pair[0]);
  const lat = Number(pair[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  return { lat, lng };
}

function partsForGeometry(
  geometry: LandGateGeoJsonGeometry,
): Array<{ type: SiteLayer['geometry']['type']; coordinates: { lat: number; lng: number }[] }> {
  if (geometry.type === 'Point') {
    const point = latLng(geometry.coordinates);
    return point ? [{ type: 'point', coordinates: [point] }] : [];
  }
  if (geometry.type === 'LineString') {
    const coordinates = geometry.coordinates.map(latLng).filter((v): v is { lat: number; lng: number } => !!v);
    return coordinates.length >= 2 ? [{ type: 'line', coordinates }] : [];
  }
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  return polygons.flatMap(polygon => {
    const outer = polygon[0] ?? [];
    const coordinates = outer.map(latLng).filter((v): v is { lat: number; lng: number } => !!v);
    if (coordinates.length > 1) {
      const first = coordinates[0]!;
      const last = coordinates[coordinates.length - 1]!;
      if (first.lat === last.lat && first.lng === last.lng) coordinates.pop();
    }
    return coordinates.length >= 3 ? [{ type: 'polygon' as const, coordinates }] : [];
  });
}

function stableFeatureId(feature: LandGateGeoJsonFeature, index: number): string {
  const props = feature.properties ?? {};
  const raw = feature.id ?? props.feature_id ?? props.parcel_id ?? props.apn ?? `feature-${index + 1}`;
  return String(raw).replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 100);
}

export function landGateFeatureToSiteLayers(
  feature: LandGateGeoJsonFeature,
  featureIndex: number,
  meta: Pick<LandGateSiteSearchResponse, 'retrievedAt' | 'sourceUrl' | 'connection'>,
): SiteLayer[] {
  if (!feature.geometry) return [];
  const properties = feature.properties ?? {};
  const featureId = stableFeatureId(feature, featureIndex);
  const name = safeText(properties.name ?? properties.label ?? properties.parcel_id ?? properties.apn)
    ?? `LandGate feature ${featureIndex + 1}`;
  const category = categoryForFeature(feature);
  const attributes = safeAttributes(properties);
  const dataVintage = safeText(properties.data_vintage ?? properties.source_date);
  return partsForGeometry(feature.geometry).map((geometry, partIndex) => {
    const partSuffix = partIndex === 0 ? '' : `-part-${partIndex + 1}`;
    const provisional: SiteLayer = {
      id: `landgate-${featureId}${partSuffix}`,
      name: partIndex === 0 ? name : `${name} (part ${partIndex + 1})`,
      category,
      geometry,
      source: 'landgate-import',
      importedAt: meta.retrievedAt,
      visible: true,
      ...(attributes ? { attributes } : {}),
      evidence: {
        use: 'map-only',
        source: {
          providerId: 'landgate',
          featureId,
          ...(meta.sourceUrl ? { sourceUrl: meta.sourceUrl } : {}),
          retrievedAt: meta.retrievedAt,
          ...(dataVintage ? { dataAsOf: dataVintage } : {}),
          providerConnectionId: meta.connection.providerConnectionId,
          credentialVersion: meta.connection.credentialVersion,
          connectionScope: meta.connection.connectionScope,
          contractVersion: meta.connection.contractVersion,
          // Review identity follows provider-owned evidence, not mutable map
          // presentation or fetch time. A no-change refetch can therefore
          // retain review, while geometry/attribute/vintage changes cannot.
          evidenceHash: hashCanonicalJson({
            schemaVersion: 1,
            providerId: 'landgate',
            featureId,
            partIndex,
            name,
            category,
            geometry,
            attributes: attributes ?? null,
            dataAsOf: dataVintage ?? null,
            providerConnectionId: meta.connection.providerConnectionId,
            credentialVersion: meta.connection.credentialVersion,
            connectionScope: meta.connection.connectionScope,
            contractVersion: meta.connection.contractVersion,
          }),
        },
        promotion: { state: 'map-only', facts: [], history: [] },
      },
    };
    return { ...provisional, contentHash: computeLayerContentHash(provisional) };
  });
}

export function landGateResponseToSiteLayers(response: LandGateSiteSearchResponse): SiteLayer[] {
  return response.featureCollection.features.flatMap((feature, index) => (
    landGateFeatureToSiteLayers(feature, index, response)
  ));
}

export async function fetchLandGateSiteLayers(
  siteId: string,
  ownerUid?: string,
  options: { signal?: AbortSignal } = {},
): Promise<{ layers: SiteLayer[]; warnings: string[] }> {
  if (!isLandGateImportEnabled()) throw new Error('LandGate import is disabled in this deployment');
  const query = ownerUid ? `?ownerUid=${encodeURIComponent(ownerUid)}` : '';
  const res = await authedFetch(`/api/landgate/sites/${encodeURIComponent(siteId)}/search${query}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal: options.signal,
  });
  if (!res.ok) throw await landGateConnectionErrorFromResponse(res);
  const json = await res.json().catch(() => ({})) as LandGateSiteSearchResponse;
  return { layers: landGateResponseToSiteLayers(json), warnings: json.warnings ?? [] };
}

export function mergeLandGateLayers(
  existing: SiteLayer[],
  incoming: SiteLayer[],
): {
  merged: SiteLayer[];
  added: SiteLayer[];
  skipped: SiteLayer[];
  refreshed: SiteLayer[];
  reviewInvalidated: SiteLayer[];
} {
  // Keep the generic id/hash decision so LandGate imports retain KMZ dedup
  // compatibility, then replace matched LandGate rows with a source-refresh
  // merge. Generic imported-layer merging is deliberately existing-wins and
  // would otherwise leave stale vendor geometry, attributes and provenance.
  const base = mergeImportedLayers(existing, incoming);
  const merged = [...base.merged];
  const refreshedByIndex = new Map<number, SiteLayer>();
  const invalidatedByIndex = new Map<number, SiteLayer>();
  const existingHashes = existing.map(layer => (
    layer.contentHash ?? computeLayerContentHash(layer)
  ));

  for (const candidate of incoming) {
    const candidateHash = candidate.contentHash ?? computeLayerContentHash(candidate);
    let existingIndex = candidate.id
      ? existing.findIndex(layer => layer.id === candidate.id)
      : -1;
    if (existingIndex < 0) {
      existingIndex = existingHashes.findIndex(hash => hash === candidateHash);
    }
    if (existingIndex < 0) continue;

    const prior = existing[existingIndex];
    if (prior?.source !== 'landgate-import' || candidate.source !== 'landgate-import') continue;

    const priorPromotion = prior.evidence?.promotion;
    const terminalReview = priorPromotion?.history.at(-1);
    const reviewedEvidenceHash = priorPromotion?.state === 'promoted'
      && terminalReview?.action === 'promote'
      ? terminalReview.sourceFeature.evidenceHash
      : undefined;
    const incomingEvidenceHash = candidate.evidence?.source.evidenceHash;
    const canRetainPromotion = priorPromotion?.state === 'promoted'
      && Boolean(reviewedEvidenceHash)
      && reviewedEvidenceHash === incomingEvidenceHash;
    const reviewInvalidated = priorPromotion?.state === 'promoted' && !canRetainPromotion;
    const sourceRefreshChanged = hashCanonicalJson({
      geometry: prior.geometry,
      attributes: prior.attributes ?? null,
      importedAt: prior.importedAt,
      source: prior.evidence?.source ?? null,
    }) !== hashCanonicalJson({
      geometry: candidate.geometry,
      attributes: candidate.attributes ?? null,
      importedAt: candidate.importedAt,
      source: candidate.evidence?.source ?? null,
    });
    if (!sourceRefreshChanged && !reviewInvalidated) continue;

    const promotion = canRetainPromotion
      ? priorPromotion
      : priorPromotion
        ? {
            state: 'map-only' as const,
            facts: [],
            history: Array.isArray(priorPromotion.history) ? priorPromotion.history : [],
          }
        : candidate.evidence?.promotion;

    const refreshed: SiteLayer = {
      ...candidate,
      // Stable persistence identity and user-owned presentation survive.
      id: prior.id,
      name: prior.name,
      category: prior.category,
      visible: prior.visible,
      ...(prior.description !== undefined ? { description: prior.description } : {}),
      ...(prior.color !== undefined ? { color: prior.color } : {}),
      ...(prior.linkedCanvasNodeId !== undefined
        ? { linkedCanvasNodeId: prior.linkedCanvasNodeId }
        : {}),
      ...(prior.createdAt !== undefined ? { createdAt: prior.createdAt } : {}),
      ...(prior.updatedAt !== undefined ? { updatedAt: prior.updatedAt } : {}),
      evidence: {
        ...(candidate.evidence ?? {
          use: 'map-only' as const,
          source: { providerId: 'landgate', retrievedAt: candidate.importedAt },
        }),
        use: canRetainPromotion ? prior.evidence?.use ?? 'screening' : 'map-only',
        ...(promotion ? { promotion } : {}),
      },
    };
    merged[existingIndex] = refreshed;
    refreshedByIndex.set(existingIndex, refreshed);
    if (reviewInvalidated) invalidatedByIndex.set(existingIndex, refreshed);
  }

  return {
    ...base,
    merged,
    refreshed: [...refreshedByIndex.values()],
    reviewInvalidated: [...invalidatedByIndex.values()],
  };
}
